<template>
  <ResourceDialog
    ref="dialog"
    :cancellable="false"
    :max-width="null"
    save-button-text="Done"
    text-class="pa-4 bg-super-light-blue"
    close-on-save
    closeable
    fullscreen
  >
    <template #header>
      <v-row>
        <v-col
          class="px-3 py-2"
          cols="12"
        >
          <div class="d-flex align-center justify-space-between">
            <h4>
              {{ $bct('Statement ID') }} # {{ shortId(statement.id) }} |
              {{ statement?.provider_name }}
            </h4>
            <v-btn
              @click="dialog.close()"
              :aria-label="$bct('Close dialog')"
              icon="close"
              variant="text"
            />
          </div>
        </v-col>
      </v-row>
    </template>

    <template #form>
      <div class="d-flex flex-column ga-4">
        <v-row>
          <v-col cols="6">
            <StandardCard title="Summary">
              <template
                v-for="row in summaryRows"
                :key="row"
              >
                <v-divider
                  v-if="row.divider"
                  class="mt-4 mb-2"
                />

                <v-row
                  v-if="row.title"
                  class="fs-14 mt-0 align-center"
                >
                  <v-col
                    class="py-1"
                    cols="8"
                  >
                    <span>
                      {{ $bct(row.title) }}
                    </span>
                  </v-col>

                  <v-col
                    class="d-flex justify-end py-1"
                    cols="4"
                  >
                    <v-chip
                      :color="row.valueColor"
                      class="pa-0"
                      variant="text"
                    >
                      {{ row.value }}
                    </v-chip>
                  </v-col>
                </v-row>
              </template>
            </StandardCard>
          </v-col>
        </v-row>

        <v-alert
          v-if="statement.failures"
          :text="statement.failures"
          class="billing-statement-alert-prepend"
          theme="error"
          type="error"
          variant="tonal"
        >
          <template #append>
            <v-btn
              v-if="isRetryable"
              @click="retryPayment"
              :aria_label="$bct('Retry payment')"
              class="bg-red"
              size="large"
              variant="flat"
            >
              {{ $bct('Retry payment') }}
            </v-btn>
          </template>
        </v-alert>

        <BillingInvoiceIndex
          :can-open-invoices="canOpenInvoices"
          :default-providers="defaultProviders"
          :statement="statement"
        />

        <VerticalSpacer :min-height="14" />
      </div>

      <ConfirmDialog ref="confirmDialog" />
    </template>
  </ResourceDialog>
</template>

<script setup>
import BillingInvoiceIndex from '@/shared/components/billing/BillingInvoiceIndex.vue';
import ConfirmDialog from '@/shared/components/ConfirmDialog.vue';
import { currency, shortId } from '@/plugins/filters';
import ResourceDialog from '@/shared/components/form/ResourceDialog.vue';
import useRole from '@/shared/composables/useRole';
import VerticalSpacer from '@/shared/components/VerticalSpacer.vue';

const $bct = inject('translator');

const { isSpecialist } = useRole();

const props = defineProps({
  canOpenInvoices: Boolean,
  defaultProviders: {
    type: Array,
    default: () => null,
  },
  summaryCardClass: {
    type: String,
    default: null,
  },
  api: {
    type: Object,
    default: null,
  },
});

const confirmDialog = ref(null);
const dialog = ref(null);
const summaryRows = ref([]);
const statement = ref(null);

const isRetryable = computed(() => {
  return isSpecialist && statement.value.status === 'errored';
});

function currencyWithSign(value) {
  const plainCurrency = currency(value);

  if (value > 0) return `+ ${plainCurrency}`;
  if (value < 0) return `- ${plainCurrency.substring(1)}`;

  return plainCurrency;
}

async function open(billingStatementId) {
  statement.value = (await props.api.get(billingStatementId)).data;

  const credits = statement.value.invoices.reduce((accumulator, invoice) => {
    if (invoice.total_amount > 0) return accumulator + invoice.total_amount;
    return accumulator;
  }, 0);

  const debits = statement.value.invoices.reduce((accumulator, invoice) => {
    if (invoice.total_amount < 0) return accumulator + invoice.total_amount;
    return accumulator;
  }, 0);

  summaryRows.value = [
    {
      title: 'Credits subtotal',
      value: currencyWithSign(credits),
    },
    { divider: true },
    {
      title: 'Debits subtotal',
      value: currencyWithSign(debits),
    },
    { divider: true },
    {
      title: 'Statement balance',
      value: currency(credits + debits),
    },
  ];

  dialog.value.open();
}

async function retryPayment() {
  const confirm = await confirmDialog.value.confirmWithText(
    'Are you sure you want to retry this payment?',
  );
  if (!confirm) return;

  const response = await props.api.retry(statement.value.id);

  if (response.status === 200) statement.value = (await props.api.get(statement.value.id)).data;
}

defineExpose({
  open,
});
</script>

<!-- This is not scoped because the precedence fails to override vuetify -->
<style>
/* Enforces icon to be centered */
.billing-statement-alert-prepend .v-alert__prepend {
  align-self: center !important;
}
</style>
